import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck, X, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { fleet } from '../mock';

// Helper : tente d'afficher l'image fournie par le client si elle a été déposée
// dans /assets/images/vehicles/{imageName}. Sinon, affiche un placeholder propre.
const FleetImage = ({ item }) => {
  const [errored, setErrored] = useState(false);
  const src = `${process.env.PUBLIC_URL}/assets/images/vehicles/${item.imageName}`;

  if (errored) {
    return (
      <div
        data-testid={`fleet-placeholder-${item.id}`}
        className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-2 border-dashed border-slate-500"
      >
        <Truck size={56} className="text-orange-400 mb-3" />
        <p className="text-orange-300 text-xs font-mono px-3 text-center">
          [Image véhicule à insérer]
        </p>
        <p className="text-slate-400 text-[10px] font-mono mt-1 px-3 text-center break-all">
          {item.imageName}
        </p>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={item.name}
      loading="lazy"
      onError={() => setErrored(true)}
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
    />
  );
};

const Fleet = () => {
  const [filter, setFilter] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const categories = ['all', ...Array.from(new Set(fleet.map((f) => f.category)))];
  const filtered =
    filter === 'all' ? fleet : fleet.filter((f) => f.category === filter);

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prev = () =>
    setLightboxIndex((i) => (i - 1 + filtered.length) % filtered.length);
  const next = () => setLightboxIndex((i) => (i + 1) % filtered.length);

  const current = lightboxIndex !== null ? filtered[lightboxIndex] : null;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block mb-4 px-4 py-2 bg-orange-500/20 text-orange-300 rounded-full text-sm font-semibold">
            FLOTTE & ENGINS LOURDS
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Notre <span className="text-orange-400">Parc d'Engins</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Une flotte moderne, entretenue en atelier intégré et déployable
            partout au Gabon. Parc régulièrement renouvelé — sur devis pour
            toute demande spécifique.
          </p>
        </div>
      </section>

      {/* Filtres */}
      <section className="py-10 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2 md:gap-3 justify-center mb-10">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`fleet-filter-${cat}`}
                onClick={() => setFilter(cat)}
                className={`px-4 md:px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  filter === cat
                    ? 'bg-orange-500 text-white shadow-lg scale-105'
                    : 'bg-white text-slate-700 hover:bg-slate-100 shadow'
                }`}
              >
                {cat === 'all' ? 'Tout le parc' : cat}
              </button>
            ))}
          </div>

          {/* Grille parc */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item, i) => (
              <Card
                key={item.id}
                data-testid={`fleet-card-${item.id}`}
                className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group cursor-pointer"
                onClick={() => openLightbox(i)}
              >
                <div className="relative h-56 bg-slate-800 overflow-hidden">
                  <FleetImage item={item} />
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-orange-500 hover:bg-orange-600 text-white">
                      {item.category}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-orange-500 transition-colors">
                    {item.name}
                  </h3>
                  <p className="text-orange-600 font-semibold text-sm mb-3">
                    {item.capacity}
                  </p>
                  <div className="text-sm text-slate-600 space-y-1">
                    <p>
                      <span className="font-medium">Année :</span> {item.year}
                    </p>
                    <p>
                      <span className="font-medium">Utilisation :</span>{' '}
                      {item.usage}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {current && (
        <div
          data-testid="fleet-lightbox"
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            data-testid="fleet-lightbox-close"
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-orange-400 z-10"
            aria-label="Fermer"
          >
            <X size={32} />
          </button>
          <button
            data-testid="fleet-lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-4 md:left-8 text-white hover:text-orange-400 z-10"
            aria-label="Précédent"
          >
            <ChevronLeft size={48} />
          </button>
          <div
            className="max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full aspect-video bg-slate-800 rounded-lg overflow-hidden">
              <FleetImage item={current} />
            </div>
            <div className="bg-white rounded-b-lg p-5 mt-0">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="text-2xl font-bold text-slate-900">
                  {current.name}
                </h3>
                <Badge className="bg-orange-500 text-white">
                  {current.category}
                </Badge>
              </div>
              <p className="text-orange-600 font-semibold mb-2">
                {current.capacity}
              </p>
              <p className="text-slate-700">
                <strong>Utilisation typique :</strong> {current.usage}
              </p>
              <p className="text-slate-500 text-sm mt-2">
                Année de mise en service : {current.year}
              </p>
            </div>
          </div>
          <button
            data-testid="fleet-lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-4 md:right-8 text-white hover:text-orange-400 z-10"
            aria-label="Suivant"
          >
            <ChevronRight size={48} />
          </button>
        </div>
      )}

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Un besoin spécifique ? Parlons-en.
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Camion citerne, convoi exceptionnel, contrat longue durée :
            chaque dossier fait l'objet d'une étude dédiée.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <Link to="/contact" data-testid="fleet-cta-contact">
              Demander un Devis
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Fleet;
